import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import type { LookupItem } from '../../store/api/saleContractApi'
import {
  type SaleAmendment,
  type SaleAmendmentPayload,
  useCreateSaleAmendmentMutation,
  useGetSaleAmendmentQuery,
  useGetSaleAmendmentContractSnapshotQuery,
  useLazyGetSaleAmendmentContractsQuery,
  useUpdateSaleAmendmentMutation,
} from '../../store/api/saleAmendmentsApi'
import DateTextField from '../../components/DateTextField'
import { formatDate } from '../../utils/date'
import { usePagePermission } from '../../hooks/usePagePermission'
import { HIDE_ON_MOBILE_INLINE, TEXT_TRUNCATE } from '../../theme/responsive'

interface SaleAmendmentFormDialogProps {
  open: boolean
  mode: 'new' | 'edit'
  amendmentNo?: string
  onClose: () => void
}

const fmtNumStr = (s: string): string => {
  if (!s) return ''
  const neg = s.startsWith('-')
  const abs = neg ? s.slice(1) : s
  const [intPart, ...rest] = abs.split('.')
  const intFormatted = intPart ? Number(intPart).toLocaleString('en-US') : '0'
  const result = rest.length > 0 ? `${intFormatted}.${rest.join('.')}` : intFormatted
  return neg ? `-${result}` : result
}

const numSx = { '& input': { textAlign: 'right' as const } } as const

interface SaleAmendmentFormModel {
  amendmentNo: string
  amendmentDate: string
  contractNo: string
  contentBeforeChange: string
  contentAfterChange: string
  note: string
  price: string
  quantity: string
}

const today = () => new Date().toISOString().slice(0, 10)

const emptyForm = (): SaleAmendmentFormModel => ({
  amendmentNo: '',
  amendmentDate: today(),
  contractNo: '',
  contentBeforeChange: '',
  contentAfterChange: '',
  note: '',
  price: '',
  quantity: '',
})

const toNum = (v: string): number | null => {
  if (!v) return null
  const n = Number(v)
  return isNaN(n) ? null : n
}

const safeDate = (v: unknown): string => {
  if (!v) return ''
  const s = String(v)
  return s.length >= 10 ? s.slice(0, 10) : s
}

const amendmentToForm = (a: SaleAmendment): SaleAmendmentFormModel => ({
  amendmentNo: a.amendmentNo,
  amendmentDate: safeDate(a.amendmentDate),
  contractNo: a.contractNo,
  contentBeforeChange: a.contentBeforeChange ?? '',
  contentAfterChange: a.contentAfterChange ?? '',
  note: a.note ?? '',
  price: a.price != null ? String(a.price) : '',
  quantity: a.quantity != null ? String(a.quantity) : '',
})

const formToPayload = (f: SaleAmendmentFormModel): SaleAmendmentPayload => ({
  amendmentNo: f.amendmentNo,
  amendmentDate: f.amendmentDate,
  contractNo: f.contractNo,
  contentBeforeChange: f.contentBeforeChange || null,
  contentAfterChange: f.contentAfterChange || null,
  note: f.note || null,
  price: toNum(f.price),
  quantity: toNum(f.quantity),
})

const extractApiErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object' || !('data' in error)) return 'Failed to save amendment. Please try again.'
  const data = (error as { data?: unknown }).data
  if (!data || typeof data !== 'object') return 'Failed to save amendment. Please try again.'
  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0]
    if (typeof first === 'string') return first
    if (first && typeof first === 'object' && 'msg' in first) {
      const msg = (first as { msg?: unknown }).msg
      if (typeof msg === 'string') return msg
    }
    return JSON.stringify(first)
  }
  return 'Failed to save amendment. Please try again.'
}

// Skill `/convert-form` readonly standard: neutral gray thay vì `action.hover`
// (overlay phớt hồng trên theme TLC cream).
const roSx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': {
    color: 'text.secondary',
    fontWeight: 600,
    cursor: 'default',
  },
}

export default function SaleAmendmentFormDialog({ open, mode, amendmentNo, onClose }: SaleAmendmentFormDialogProps) {
  const { canEdit } = usePagePermission()
  const [form, setForm] = useState<SaleAmendmentFormModel>(emptyForm)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [fetchContracts, { data: contractOptions, isFetching: contractsLoading }] = useLazyGetSaleAmendmentContractsQuery()
  const { data: editData, isFetching: editLoading } = useGetSaleAmendmentQuery(amendmentNo ?? '', {
    skip: !open || mode !== 'edit' || !amendmentNo,
  })
  const { data: snapshot } = useGetSaleAmendmentContractSnapshotQuery(form.contractNo, {
    skip: !open || !form.contractNo,
    // Force fresh fetch khi remount dialog / đổi contractNo — contract có thể bị
    // sửa server-side giữa các lần mở dialog. Bypass RTK Query cache để snapshot
    // (qty/price/type/...) luôn là latest.
    refetchOnMountOrArgChange: true,
  })
  const [createAmendment, { isLoading: creating }] = useCreateSaleAmendmentMutation()
  const [updateAmendment, { isLoading: updating }] = useUpdateSaleAmendmentMutation()
  const isBusy = creating || updating || editLoading

  const contractItems = useMemo(() => contractOptions?.items ?? [], [contractOptions])

  useEffect(() => {
    if (!open) return
    setValidationError(null)
    setFieldErrors({})
    if (mode === 'new') {
      setForm(emptyForm())
      fetchContracts('')
    }
  }, [open, mode, fetchContracts])

  useEffect(() => {
    if (open && mode === 'edit' && editData) {
      setForm(amendmentToForm(editData))
    }
  }, [open, mode, editData])

  const clearFieldError = (key: string) => {
    setFieldErrors((prev) => {
      if (!(key in prev)) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  const set = <K extends keyof SaleAmendmentFormModel>(key: K, value: SaleAmendmentFormModel[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    clearFieldError(key as string)
  }

  const setNum = (key: keyof SaleAmendmentFormModel, raw: string) => {
    setForm((prev) => ({ ...prev, [key]: raw.replace(/,/g, '') }))
    clearFieldError(key as string)
  }

  const validateLikeAccess = (): { message: string | null; errors: Record<string, string> } => {
    const errors: Record<string, string> = {}
    const messages: string[] = []
    const addError = (k: string, m: string) => {
      if (!errors[k]) {
        errors[k] = m
        messages.push(m)
      }
    }
    if (!form.contractNo.trim()) addError('contractNo', 'Please choose Contract No!')
    if (!form.amendmentNo.trim()) addError('amendmentNo', 'Please type Amendment No!')
    if (!form.amendmentDate) addError('amendmentDate', 'Please choose Amendment Date!')
    return { message: messages[0] ?? null, errors }
  }

  const handleSave = async () => {
    const { message, errors } = validateLikeAccess()
    setFieldErrors(errors)
    if (message || Object.keys(errors).length > 0) {
      setValidationError(message ?? 'Please fix the highlighted fields.')
      return
    }
    setValidationError(null)
    try {
      const body = formToPayload(form)
      if (mode === 'edit' && amendmentNo) {
        await updateAmendment({ amendmentNo, body }).unwrap()
      } else {
        await createAmendment(body).unwrap()
      }
      onClose()
    } catch (error) {
      setValidationError(extractApiErrorMessage(error))
    }
  }

  const handleCancel = () => {
    setForm(emptyForm())
    setValidationError(null)
    setFieldErrors({})
    onClose()
  }

  // Pull contract context from either the live snapshot query or the edit prefill
  const ctx = {
    vendor: snapshot?.contractClientName ?? editData?.contractSnapshot?.contractClientName ?? '',
    contractDate: safeDate(snapshot?.contractDate ?? editData?.contractSnapshot?.contractDate),
    contractType: snapshot?.contractType ?? editData?.contractSnapshot?.contractType ?? '',
    quality: snapshot?.contractQuality ?? editData?.contractSnapshot?.contractQuality ?? '',
    contractQty: snapshot?.contractQuantity ?? editData?.contractSnapshot?.contractQuantity ?? '',
    priceUnit: snapshot?.contractPriceUnit ?? editData?.contractSnapshot?.contractPriceUnit ?? '',
    bags: snapshot?.contractBags ?? editData?.contractSnapshot?.contractBags ?? '',
    outrightPrice: snapshot?.contractOutrightPrice ?? editData?.contractSnapshot?.contractOutrightPrice ?? '',
    ptbfDiffs: snapshot?.contractPtbfDiffs ?? editData?.contractSnapshot?.contractPtbfDiffs ?? '',
  }

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 1, pt: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          component="span"
          noWrap
          title="Sales Contract - Amendment"
          sx={{ ...TEXT_TRUNCATE, flex: 1, fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
            Sales Contract -{' '}
          </Box>
          Amendment
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={handleSave}
            disabled={isBusy || !canEdit}
            sx={{ py: 0.25, px: 1, minHeight: 28, fontSize: '0.75rem', lineHeight: 1.2, textTransform: 'none' }}
          >
            {isBusy ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleCancel}
            sx={{ py: 0.25, px: 1, minHeight: 28, fontSize: '0.75rem', lineHeight: 1.2, textTransform: 'none' }}
          >
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={{
        pt: 0.75,
        '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
        '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
        '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
        '& .MuiInputLabel-shrink': { fontWeight: 600 },
        '& .MuiFormControlLabel-label': { fontSize: '0.8125rem', fontWeight: 500 },
        '& .MuiCheckbox-root': { p: 0.5 },
        '& .MuiSvgIcon-root': { fontSize: '1rem' },
        '& .MuiTypography-body2': { fontSize: '0.8125rem' },
        '& .MuiSelect-select': { fontSize: '0.8125rem' },
      }}>
        {validationError && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {validationError}
          </Alert>
        )}

        {/* ── 1) Key fields ── */}
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            {mode === 'edit' ? (
              <TextField
                label="Contract No"
                size="small"
                fullWidth
                value={form.contractNo}
                InputProps={{ readOnly: true }}
                sx={roSx}
              />
            ) : (() => {
              const selectedContract: LookupItem | null = form.contractNo
                ? contractItems.find((o) => o.value === form.contractNo) ?? { value: form.contractNo, label: form.contractNo }
                : null
              return (
                <Autocomplete
                  size="small"
                  options={contractItems}
                  loading={contractsLoading}
                  value={selectedContract}
                  getOptionLabel={(o: LookupItem | string) => (typeof o === 'string' ? o : o.label)}
                  isOptionEqualToValue={(o, v) => o.value === v.value}
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input') fetchContracts(value)
                  }}
                  onChange={(_, item) => set('contractNo', item?.value ?? '')}
                  filterOptions={(x) => x}
                  clearOnBlur
                  handleHomeEndKeys
                  autoHighlight
                  selectOnFocus
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Contract No"
                      error={!!fieldErrors.contractNo}
                      helperText={fieldErrors.contractNo}
                    />
                  )}
                />
              )
            })()}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Amendment No"
              size="small"
              fullWidth
              value={form.amendmentNo}
              onChange={(e) => set('amendmentNo', e.target.value)}
              InputProps={{ readOnly: mode === 'edit' }}
              sx={mode === 'edit' ? roSx : undefined}
              error={!!fieldErrors.amendmentNo}
              helperText={fieldErrors.amendmentNo}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DateTextField
              label="Amendment Date"
              size="small"
              fullWidth
              value={form.amendmentDate}
              onChange={(value) => set('amendmentDate', value)}
              error={!!fieldErrors.amendmentDate}
              helperText={fieldErrors.amendmentDate}
            />
          </Grid>
        </Grid>

        {/* ── 2) Contract snapshot context ── */}
        <Box sx={{ mt: 2, mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.68rem' }}>
            Contract Reference
          </Typography>
          <Divider sx={{ mt: 0.5 }} />
        </Box>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}>
            <TextField label="Vendor" size="small" fullWidth value={ctx.vendor} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Contract Date" size="small" fullWidth value={formatDate(ctx.contractDate)} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Contract Type" size="small" fullWidth value={ctx.contractType} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Quality" size="small" fullWidth value={ctx.quality} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="No of bags" size="small" fullWidth value={ctx.bags} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Contract Quantity" size="small" fullWidth value={ctx.contractQty} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Price Unit" size="small" fullWidth value={ctx.priceUnit} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Outright Price" size="small" fullWidth value={ctx.outrightPrice} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="PTBF Diffs" size="small" fullWidth value={ctx.ptbfDiffs} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={roSx} />
          </Grid>
        </Grid>

        {/* ── 3) Editable amendment details ── */}
        <Box sx={{ mt: 2, mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.68rem' }}>
            Amendment Details
          </Typography>
          <Divider sx={{ mt: 0.5 }} />
        </Box>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label="Content Before Change"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={form.contentBeforeChange}
              onChange={(e) => set('contentBeforeChange', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Content After Change"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={form.contentAfterChange}
              onChange={(e) => set('contentAfterChange', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Note"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={form.note}
              onChange={(e) => set('note', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Price"
              size="small"
              fullWidth
              value={fmtNumStr(form.price)}
              onChange={(e) => setNum('price', e.target.value)}
              inputProps={{ inputMode: 'decimal' }}
              sx={numSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Quantity"
              size="small"
              fullWidth
              value={fmtNumStr(form.quantity)}
              onChange={(e) => setNum('quantity', e.target.value)}
              inputProps={{ inputMode: 'decimal' }}
              sx={numSx}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
